import { useEffect, useState } from 'react'
import api from '@/http/api'
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData
} from '@tanstack/react-query'
import ExploreCatalog, {
  type ExploreCatalogParams
} from '@univdiam/ui/customComponents/Catalog/ExploreCatalog'
import CatalogManagementTable, {
  type CatalogProductItem,
  type CatalogVariantItem
} from '@univdiam/ui/customComponents/Catalog/CatalogManagementTable'
import AddCatalogProduct, {
  type CatalogModel
} from '@univdiam/ui/customComponents/forms/AddCatalogProduct'
import SyncCatalogModal from '@univdiam/ui/customComponents/Catalog/SyncCatalogModal'
import Drawer from '@univdiam/ui/customComponents/Drawer'
import { Tabs, TabsList, TabsTrigger } from '@univdiam/ui/components/tabs'
import { useNavigate } from 'react-router-dom'
import { LayoutGrid, TableProperties } from 'lucide-react'
import { toast } from 'sonner'

const fetchCatalog = (params: ExploreCatalogParams) =>
  api.get('/catalog/', { params }).then((res) => res.data)

function Catalog() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Active Top Tab: 'showcase' | 'inventory'
  const [activeTab, setActiveTab] = useState<'showcase' | 'inventory'>('inventory')

  // Add-product drawer / import sheet dialog
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false)
  const [mediaLoading, setMediaLoading] = useState(false)

  // ==========================================
  // TAB 1: Showcase (Explore) View State
  // ==========================================
  const handleNavigate = (sku: string) => {
    navigate(`/catalog-product/${sku}`)
  }

  // ==========================================
  // TAB 2: Inventory & Variant Manager State
  // ==========================================
  const [mgmtSearch, setMgmtSearch] = useState('')
  const [mgmtDebouncedSearch, setMgmtDebouncedSearch] = useState('')
  const [mgmtType, setMgmtType] = useState('all')
  const [mgmtMetal, setMgmtMetal] = useState('all')
  const [mgmtStatus, setMgmtStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  useEffect(() => {
    const t = setTimeout(() => {
      setMgmtDebouncedSearch(mgmtSearch)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [mgmtSearch])

  const {
    data: adminData,
    isLoading: isAdminLoading,
    refetch: refetchAdminItems
  } = useQuery({
    queryKey: [
      'admin-catalog-items',
      page,
      pageSize,
      mgmtDebouncedSearch,
      mgmtType,
      mgmtMetal,
      mgmtStatus
    ],
    queryFn: async () => {
      const res = await api.get('/catalog/admin/items', {
        params: {
          page,
          limit: pageSize,
          search: mgmtDebouncedSearch,
          type: mgmtType,
          metal: mgmtMetal,
          status: mgmtStatus
        }
      })
      return res.data
    },
    placeholderData: keepPreviousData
  })

  const adminItems: CatalogProductItem[] = adminData?.items || []
  const totalItems: number = adminData?.pagination?.totalItems || 0
  const typeOptions: string[] = adminData?.facets?.types || []
  const metalOptions: string[] = adminData?.facets?.metals || []
  // Metal labels, diamond types and karat tiers come from catalogModel.js, so
  // the form cannot drift from the strings actually stored in `stock`.
  const catalogModel: CatalogModel | undefined = adminData?.model

  // Hand uploads go through the sheet import's media pipeline (webp + thumbnail
  // under catalog/), with unique names so two IMG_1234.jpg never share a key.
  const handleSendToUpload = async (files: File[]): Promise<string[]> => {
    setMediaLoading(true)
    const urls: string[] = []
    try {
      // The endpoint takes at most 10 files per request.
      for (let i = 0; i < files.length; i += 10) {
        const formData = new FormData()
        formData.append('unique', 'true')
        files.slice(i, i + 10).forEach((file) => formData.append('files', file))
        const res = await api.post('/catalog/admin/sync-csv/media', formData)
        if (res.data?.failed?.length) {
          toast.error(`${res.data.failed.length} file(s) failed to upload`)
        }
        urls.push(...(res.data?.urls || []))
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed')
    } finally {
      setMediaLoading(false)
    }
    return urls
  }

  // Mutations
  const updateVariantMutation = useMutation({
    mutationFn: async ({
      stockId,
      data
    }: {
      stockId: number
      data: Partial<CatalogVariantItem>
    }) => {
      const res = await api.patch(`/catalog/admin/variant/${stockId}`, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-catalog-items'] })
      queryClient.invalidateQueries({ queryKey: ['catalog-categories'] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to update variant')
    }
  })

  const deleteVariantMutation = useMutation({
    mutationFn: async ({
      stockId,
      hard
    }: {
      stockId: number
      hard?: boolean
    }) => {
      const res = await api.delete(
        `/catalog/admin/variant/${stockId}?hard=${hard ? 'true' : 'false'}`
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-catalog-items'] })
      queryClient.invalidateQueries({ queryKey: ['catalog-categories'] })
    }
  })

  const createProductMutation = useMutation({
    mutationFn: async (productData: any) => {
      const res = await api.post('/catalog/admin/product', productData)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-catalog-items'] })
      queryClient.invalidateQueries({ queryKey: ['catalog-categories'] })
    }
  })

  // Step 1: dry run. Nothing is written; we get the change report back.
  const handleAnalyzeCSV = async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.post('/catalog/admin/sync-csv/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return res.data?.report
  }

  // Step 2: only the media files the report said were missing.
  const handleUploadMedia = async (files: File[]) => {
    const formData = new FormData()
    files.forEach((f) => formData.append('files', f))
    const res = await api.post('/catalog/admin/sync-csv/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return res.data
  }

  // Step 3: apply.
  const handleSyncCSV = async (file: File, deactivateMissing: boolean) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('deactivateMissing', String(deactivateMissing))

    const res = await api.post('/catalog/admin/sync-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    queryClient.invalidateQueries({ queryKey: ['admin-catalog-items'] })
    queryClient.invalidateQueries({ queryKey: ['catalog-categories'] })
    return res.data?.stats
  }

  return (
    <div>
      {/* Tab switcher, aligned to the card gutter the rest of the app uses */}
      <div className="mx-4 mt-4 md:mx-[24px] md:mt-[24px]">
        <Tabs
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as 'showcase' | 'inventory')}
        >
          <TabsList>
            <TabsTrigger value="inventory">
              <TableProperties className="h-4 w-4" />
              Inventory &amp; Variants
            </TabsTrigger>
            <TabsTrigger value="showcase">
              <LayoutGrid className="h-4 w-4" />
              Showcase &amp; Share
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* TAB 1: Inventory & Variant Manager */}
      {activeTab === 'inventory' && (
        <CatalogManagementTable
          items={adminItems}
          totalItems={totalItems}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          isLoading={isAdminLoading}
          onRefresh={() => refetchAdminItems()}
          search={mgmtSearch}
          onSearchChange={setMgmtSearch}
          typeFilter={mgmtType}
          onTypeFilterChange={(val) => {
            setMgmtType(val)
            setPage(1)
          }}
          metalFilter={mgmtMetal}
          onMetalFilterChange={(val) => {
            setMgmtMetal(val)
            setPage(1)
          }}
          statusFilter={mgmtStatus}
          onStatusFilterChange={(val) => {
            setMgmtStatus(val)
            setPage(1)
          }}
          typeOptions={typeOptions}
          metalOptions={metalOptions}
          onUpdateVariant={async (stockId, data) => {
            await updateVariantMutation.mutateAsync({ stockId, data })
          }}
          onDeleteVariant={async (stockId, hard) => {
            await deleteVariantMutation.mutateAsync({ stockId, hard })
          }}
          onOpenAddProduct={() => setIsAddDrawerOpen(true)}
          onOpenSyncModal={() => setIsSyncModalOpen(true)}
          onUploadMedia={handleSendToUpload}
        />
      )}

      {/* TAB 2: Showcase / Explore View */}
      {activeTab === 'showcase' && (
        <div>
          <ExploreCatalog
            handleNavigate={handleNavigate}
            fetchCatalog={fetchCatalog}
          />
        </div>
      )}

      <Drawer
        open={isAddDrawerOpen}
        setOpen={setIsAddDrawerOpen}
        side="right"
        title="Add product"
        contentClassName="lg:max-w-[900px]"
      >
        {catalogModel && (
          <AddCatalogProduct
            model={catalogModel}
            mediaLoading={mediaLoading}
            isSubmitting={createProductMutation.isPending}
            handleSendToUpload={handleSendToUpload}
            onSubmit={async (payload) => {
              try {
                const res = await createProductMutation.mutateAsync(payload)
                toast.success(`Created ${res?.count ?? 0} variants`)
                setIsAddDrawerOpen(false)
              } catch (err: any) {
                toast.error(
                  err?.response?.data?.error || 'Failed to create product'
                )
              }
            }}
          />
        )}
      </Drawer>

      {/* Sync CSV Modal */}
      <SyncCatalogModal
        open={isSyncModalOpen}
        onOpenChange={setIsSyncModalOpen}
        onAnalyze={handleAnalyzeCSV}
        onUploadMedia={handleUploadMedia}
        onApply={handleSyncCSV}
        onSuccess={() => {
          refetchAdminItems()
        }}
      />
    </div>
  )
}

export default Catalog
